import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import '@adornis/entity-picker/client/x-multi-entity-picker.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { AbstractAspect } from '@adornis/filter/aspect-schemas/AbstractAspect.js';
import type { AspectConfig } from '@adornis/filter/filter-options.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import '@adornis/popover/x-dropdown-multiselection.js';
import { html, nothing } from 'lit';
import { translationDict } from '../../db/translation-dict.js';
import { GroupsAPI } from '../../groups/api/GroupsAPI.js';
import { Group } from '../../groups/entities/Group.js';
import { Role } from '../roles.js';

const modeDisplay = {
  and: 'alle Rollen',
  or: 'eine Rolle',
  not: 'keine Rolle',
};

const modeDisplayContext = {
  and: 'in allen Schulen',
  or: 'in einer Schule',
};

function buildAllCombinations(names: string[], contexts: string[]): { name: string; contexts: string }[] {
  const result: { name: string; contexts: string }[] = [];

  // custom case if no things are selected
  if (names.length !== 0 && contexts.length === 0) {
    return buildAndName(names, []).map(combination => ({ name: combination.name, contexts: '' }));
  }

  if (contexts.length !== 0 && names.length === 0) {
    return buildAndContexts([], contexts).map(combination => ({ name: '', contexts: combination.contexts }));
  }

  for (const name of names) {
    for (const context of contexts) {
      result.push({ name, contexts: context });
    }
  }
  return result;
}

function buildAndName(names: string[], contexts: string[]): { name: string; contexts: string[] }[] {
  const result: { name: string; contexts: string[] }[] = [];

  // if the context is empty we can return a single object with the name
  if (names.length === 0) {
    return [{ name: '', contexts }];
  }

  for (const name of names) {
    result.push({ name, contexts });
  }
  return result;
}

function buildAndContexts(names: string[], contexts: string[]): { name: string[]; contexts: string }[] {
  const result: { name: string[]; contexts: string }[] = [];

  // if the context is empty we can return a single object with the name
  if (contexts.length === 0) {
    return [{ name: names, contexts: '' }];
  }

  for (const context of contexts) {
    result.push({ name: names, contexts: context });
  }
  return result;
}

const combinationFunctions = {
  or: {
    and: buildAndContexts,
  },
  and: {
    or: buildAndName,
    and: buildAllCombinations,
  },
};

@Entity()
export class RoleFilterAspect extends AbstractAspect {
  static override _class = 'RoleFilterAspect';

  @Field(() => String, { default: v => v ?? 'or' })
  mode!: keyof typeof modeDisplay;

  @Field(() => String, { default: v => v ?? 'or' })
  modeContext!: keyof typeof modeDisplayContext;

  // the _id of the role
  @Field(() => [String], { default: v => v ?? [] }) value: Maybe<string[]>;

  @Field(() => [String], { default: v => v ?? [] }) context: Maybe<string[]>;

  // only used for displaying in the aspect
  @Field(() => String) name: Maybe<string>;

  // only used for displaying in the aspect
  @Field(() => String) contextName: Maybe<string>;

  constructor(doc: PartialEntityData<RoleFilterAspect>) {
    super(doc);
  }

  override getMongoFilter(): object | null {
    if (!this.fieldName) return null;

    const selectors: Record<string, any>[] = [];

    if (this.mode === 'not') {
      return {
        [`${this.fieldName}.name`]: {
          $not: {
            $in: Object.values(Role),
          },
        },
      };
    }

    if (this.mode === 'or' && this.modeContext === 'or') {
      // if both values are or we can use $in on a single field and do not need to use combinations
      const subSel: Record<string, any> = {};
      if (this.value?.length) subSel.name = { $in: this.value };
      if (this.context?.length) subSel.contexts = { $in: this.context };
      if (Object.keys(subSel).length) selectors.push({ $elemMatch: subSel });
    } else {
      const combinationForAllContexts = combinationFunctions[this.mode][this.modeContext](
        this.value ?? [],
        this.context ?? [],
      );
      if (!combinationForAllContexts.length) return null;
      for (const combination of combinationForAllContexts) {
        const subSel: Record<string, any> = {};
        const makeArray = (value: string | string[]) => {
          if (Array.isArray(value)) return value;
          return [value];
        };

        if (this.value?.length) subSel.name = { $in: makeArray(combination.name) };
        if (this.context?.length) subSel.contexts = { $in: makeArray(combination.contexts) };
        if (Object.keys(subSel).length) selectors.push({ $elemMatch: subSel });
      }
    }

    if (!selectors.length) return null;

    return {
      [this.fieldName]: { $all: selectors },
    };
  }
}

export const RoleFilterAspectSchema: (options?: {
  operatingFieldType?: 'atomic' | 'object';
  filter?: AdornisFilter;
}) => AspectConfig<RoleFilterAspect> = options => ({
  aspectConstructor: RoleFilterAspect,
  renderEditor: ({ aspectValue }) => {
    options ??= { operatingFieldType: 'atomic' };

    options.filter ??= new AdornisFilter({ searchFields: ['name', '_class'] });

    if (!options.filter.searchFields?.length) {
      options.filter.searchFields = ['name', '_class'];
    }

    return html`
      <div class="flex flex-wrap gap-2">
        <div class="flex gap-2 flex-1 min-w-[300px]">
          <div class="flex flex-1 min-w-[100px]">
            ${DropdownSelectionInput({
              value: aspectValue.mode,
              onPicked: value => {
                aspectValue.mode = value ?? 'or';
              },
              renderString: (key: keyof typeof modeDisplay) => modeDisplay[key],
              selectables: Object.keys(modeDisplay) as (keyof typeof modeDisplay)[],
            })}
          </div>
          ${aspectValue.mode === 'not'
            ? nothing
            : html`
                <div class="flex flex-2 min-w-[200px]">
                  ${DropdownSelectionInput({
                    value: aspectValue.value,
                    multi: true,
                    placeholder: 'beliebig',
                    placeholderMode: 'hide',
                    onPicked: value => {
                      console.log('value', value);
                      aspectValue.value = value;
                      aspectValue.name = value?.join(', ') ?? null;
                    },
                    search: s => {
                      if (!s) return Object.values(Role) as string[];
                      return Object.values(Role).filter(r => r.toLowerCase().includes(s.toLowerCase()));
                    },
                  })}
                </div>
              `}
        </div>
        ${aspectValue.mode === 'not'
          ? nothing
          : html`
              <div class="flex gap-2 flex-1 min-w-[300px]">
                <div class="flex flex-1 min-w-[100px]">
                  ${DropdownSelectionInput({
                    value: aspectValue.modeContext,
                    renderString: (key: keyof typeof modeDisplayContext) => modeDisplayContext[key],
                    selectables: Object.keys(modeDisplayContext) as (keyof typeof modeDisplayContext)[],
                    onPicked: value => {
                      aspectValue.modeContext = value ?? 'or';
                    },
                  })}
                </div>
                <div class="flex flex-2 min-w-[200px]">
                  ${EntityPickerInput<typeof Group, true>({
                    class: Group,
                    operation: GroupsAPI.subscribeGroups,
                    multi: true,
                    filter: options.filter,
                    placeholder: 'beliebig',
                    placeholderMode: 'hide',
                    renderString: group => `${group.name} (${translationDict._class[group._class]?.[0] as string})`,
                    selectionSet: { name: 1, _id: 1 },
                    value: aspectValue.context,
                    onEntityPicked: values => {
                      aspectValue.context = values?.map(g => g._id) ?? [];
                      aspectValue.contextName = values?.map(g => g.name).join(', ') ?? null;
                    },
                  })}
                </div>
              </div>
            `}
      </div>
    `;
  },
  renderPreview: ({ aspectValue }) => {
    return aspectValue.name || aspectValue.contextName
      ? html` ${aspectValue.name ?? 'beliebig'} ${aspectValue.contextName ? ` (${aspectValue.contextName})` : ''} `
      : null;
  },
});
